import React, { useMemo, useRef, useState } from 'react';
import { GripVertical, X } from 'react-bootstrap-icons';
import Loader from '../common/Loader';
import CustomerList from '../admin/manage-users/customer/CustomerList';
import useLocalization from '../../hooks/useLocalization';
import { useScreenWidth } from '../../hooks/useScreenWidth';
import DraggableModal from '../common/DraggableModal';
import CustomerProfileForm from '../../forms/CustomerProfileForm';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { toast } from 'react-toastify';
import { updateItemsInArray } from '../../helpers/global';

const MODAL_WIDTH = {
  sm: 400,
  md: 600,
  lg: 600,
  xl: 650,
  xxl: 650
};

const ADMIN_CUSTOMER_URL = id => `${process.env.REACT_APP_APP_URL}/admin/manage-users/customer?customerToView=${id}`;
const MODEL_OPEN_CLOSE_DELAY_IN_Ms = 300;

const APARTMENT_PROFILE_OPENING_POSITION = 'apartmentProfileOpeningPosition';

function apartmentProfileOpeningStorageKey(isRtl) {
  return `${APARTMENT_PROFILE_OPENING_POSITION}_${isRtl ? 'rtl' : 'ltr'}`;
}

function loadProfileEditorOrigin(isRtl) {
  try {
    const raw = localStorage.getItem(apartmentProfileOpeningStorageKey(isRtl));
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p.x === 'number' && typeof p.y === 'number') {
      return { x: p.x, y: p.y };
    }
  } catch {}
  return null;
}

function saveProfileEditorOrigin(isRtl, origin) {
  try {
    localStorage.setItem(apartmentProfileOpeningStorageKey(isRtl), JSON.stringify(origin));
  } catch {}
}

function getDefaultProfileEditorOrigin(screenWidth, modalWidthPx, isRtl) {
  const margin = 16;
  return {
    x: isRtl ? Math.max(margin, screenWidth - modalWidthPx - margin) : margin,
    y: margin
  };
}

const ModalHeaderRow = ({ handleMouseDown, onHide, title }) => (
  <div onMouseDown={handleMouseDown} className="d-flex justify-content-between align-items-center p-3 mb-2 gap-2">
    <h6 className="mb-0 d-flex gap-2 align-items-center">
      <GripVertical />
      {title}
    </h6>
    <X className="btn-close" size={30} onClick={onHide} />
  </div>
);

const ViewCustomersMatchingApartmentModal = ({
  show,
  onHide,
  showProgress,
  customers,
  onUpdateCustomerProfile,
  selectedMatchingCustomers,
  onSelectMatchingCustomers
}) => {
  const { translate, isRTL } = useLocalization();
  const { screenWidth, screenSize } = useScreenWidth();
  const [profileEditor, setProfileEditor] = useState(null);
  const [savedProfileEditorOrigin, setSavedProfileEditorOrigin] = useState(() => loadProfileEditorOrigin(isRTL));

  const modalWidthPx = MODAL_WIDTH[screenSize] ?? MODAL_WIDTH.sm;

  const defaultProfileEditorOrigin = useMemo(() => getDefaultProfileEditorOrigin(screenWidth, modalWidthPx, isRTL), [
    screenWidth,
    modalWidthPx,
    isRTL
  ]);

  const profileEditorPinnedOrigin = savedProfileEditorOrigin ?? defaultProfileEditorOrigin;

  const persistProfileEditorOrigin = origin => {
    setSavedProfileEditorOrigin(origin);
    saveProfileEditorOrigin(isRTL, origin);
  };

  const hoverCloseTimeoutRef = useRef(null);

  const scheduleHoverClose = () => {
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setProfileEditor(prev => (prev?.ui.readOnly && !prev.ui.openedByClick ? null : prev));
    }, MODEL_OPEN_CLOSE_DELAY_IN_Ms);
  };

  const cancelHoverClose = () => {
    clearTimeout(hoverCloseTimeoutRef.current);
  };

  const handleMatchingProfileMouseEnter = payload => {
    if (profileEditor?.ui.openedByClick) return;
    cancelHoverClose();
    setProfileEditor({
      selection: payload,
      ui: { readOnly: true, openedByClick: false, showProgress: false }
    });
  };

  const handleMatchingProfileMouseLeave = () => {
    scheduleHoverClose();
  };

  const handleProfileModalMouseEnter = () => {
    cancelHoverClose();
  };

  const handleProfileModalMouseLeave = () => {
    scheduleHoverClose();
  };

  const handleMatchingProfileClick = payload => {
    setProfileEditor({
      selection: payload,
      ui: { readOnly: false, openedByClick: true, showProgress: false }
    });
  };

  const closeProfileModal = () => {
    setProfileEditor(null);
  };

  const saveCustomerApartmentProfiles = async apartmentProfiles => {
    const { selection } = profileEditor ?? {};
    if (!selection) return;

    setProfileEditor(prev =>
      prev?.ui.openedByClick ? { ...prev, ui: { ...prev.ui, showProgress: true } } : prev
    );
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        _id: selection.customer._id,
        apartmentProfiles
      }
    });
    if (error) {
      setProfileEditor(prev =>
        prev?.ui.openedByClick ? { ...prev, ui: { ...prev.ui, showProgress: false } } : prev
      );
      toast.error(error);
      return;
    }
    closeProfileModal();
    onUpdateCustomerProfile(response);
  };

  const handleUpdateCustomerProfile = customerProfile => {
    return saveCustomerApartmentProfiles(
      updateItemsInArray(profileEditor.selection.customer.apartmentProfiles, customerProfile)
    );
  };

  const handleDeleteCustomerProfile = customerProfile => {
    return saveCustomerApartmentProfiles(
      profileEditor.selection.customer.apartmentProfiles?.filter(p => p?._id !== customerProfile?._id)
    );
  };

  const renderListModalHeader = ({ handleMouseDown, onHide }) => (
    <ModalHeaderRow
      handleMouseDown={handleMouseDown}
      onHide={onHide}
      title={translate('customers_matching_this_apartment')}
    />
  );

  const profileModalTitleKey = profileEditor?.ui.openedByClick ? 'edit_customer_profile' : 'customer_profile';

  const profileModalTitle =
    profileEditor?.selection.profile &&
    `${translate(profileModalTitleKey)} ${profileEditor.selection.profile} (${
      profileEditor.selection.customer?.name
    })`;

  const renderProfileModalHeader = ({ handleMouseDown, onHide }) => (
    <ModalHeaderRow
      handleMouseDown={handleMouseDown}
      onHide={onHide}
      title={profileModalTitle || translate(profileModalTitleKey)}
    />
  );

  return (
    <>
      <DraggableModal
        show={show}
        onHide={onHide}
        title={translate('customers_matching_this_apartment')}
        modalBodyClassName="p-1"
        renderHeader={renderListModalHeader}
        width={MODAL_WIDTH}
      >
        <div style={{ minHeight: 300 }} className="px-2 mt-1">
          {showProgress ? (
            <Loader />
          ) : customers?.length === 0 ? (
            <h6 className="smallFont text-center mt-2">{translate('no_matching_customer_found')}</h6>
          ) : (
            <CustomerList
              customers={customers}
              statusFilterInQuickSearch={false}
              onSelect={customer => window.open(ADMIN_CUSTOMER_URL(customer._id), '_blank')}
              onMatchingProfileClick={handleMatchingProfileClick}
              onMatchingProfileMouseEnter={handleMatchingProfileMouseEnter}
              onMatchingProfileMouseLeave={handleMatchingProfileMouseLeave}
              activeMatchingProfileId={
                profileEditor?.ui.openedByClick ? profileEditor.selection.apartmentProfile?._id : null
              }
              selectedMatchingCustomers={selectedMatchingCustomers}
              onSelectMatchingCustomers={onSelectMatchingCustomers}
              showProfiles={true}
            />
          )}
        </div>
      </DraggableModal>

      {/* profile modal */}
      <DraggableModal
        show={Boolean(profileEditor)}
        onHide={closeProfileModal}
        title={translate(profileModalTitleKey)}
        modalBodyClassName="p-1"
        renderHeader={renderProfileModalHeader}
        width={MODAL_WIDTH}
        maxModalHeight="96vh"
        hasOverlay={true}
        overlayPointerEvents={
          profileEditor?.ui.readOnly && !profileEditor.ui.openedByClick ? 'none' : 'auto'
        }
        pinnedPosition={profileEditorPinnedOrigin}
        onPinnedPositionChange={persistProfileEditorOrigin}
        onModalShellMouseEnter={handleProfileModalMouseEnter}
        onModalShellMouseLeave={handleProfileModalMouseLeave}
      >
        {profileEditor && (
          <div
            className="px-2 flex-grow-1 d-flex flex-column min-h-0"
            style={{
              pointerEvents: profileEditor.ui.showProgress ? 'none' : 'auto',
              minHeight: 0,
              flex: '1 1 0%'
            }}
          >
            <CustomerProfileForm
              editMode={true}
              readOnly={profileEditor.ui.readOnly}
              values={profileEditor.selection.apartmentProfile}
              onSubmit={handleUpdateCustomerProfile}
              onDeleteClick={handleDeleteCustomerProfile}
              showProgress={profileEditor.ui.showProgress}
            />
          </div>
        )}
      </DraggableModal>
    </>
  );
};

export default ViewCustomersMatchingApartmentModal;
